<script>
  import GanttHelper from './GanttHelper.js';
  import ChartContentRow from './ChartContentRow.svelte';
  import ResourceConnectionLines from './ResourceConnectionLines.svelte';
  import ChartLabel from './ChartLabel.svelte';
  import { derived } from 'svelte/store';
  import OrderConnectionLines from './OrderConnectionLines.svelte';
  import { onMount } from 'svelte';
  import { roundToNearestTime } from '../../utils/time.js';

  export let GanttStore

  const {
    CurrentDisplayedEndDate,
    CurrentDisplayedStartDate,
    CurrentStyle,
    DayStartTime,
    DisplayedRowCount,
    EndDate,
    EndDayDisplayTime,
    GanttChartType,
    InternalLowerSplits,
    InternalLowerSubSplits,
    RowsCurrentlyDisplayed,
    RowDownloadUrl,
    StartDate,
    StartDayDisplayTime,
    ValidEndDate,
    ValidStartDate,
    CurrentDisplayedEndDateInclDaysOff,
    GetAdjustedDateInclDaysOff,
  } = GanttStore



  // StartDateArea is the area that belongs to the dates before the scheduling start date.
  const StartDateAreaEnd = derived([StartDate, CurrentDisplayedEndDate, CurrentDisplayedStartDate, GetAdjustedDateInclDaysOff], ([StartDate, CurrentDisplayedEndDate, CurrentDisplayedStartDate, GetAdjustedDateInclDaysOff]) => {
    return Math.min(100, 100 * GanttHelper._gantHelper_scale(GetAdjustedDateInclDaysOff(StartDate).getTime(), CurrentDisplayedEndDate.getTime(), CurrentDisplayedStartDate.getTime()))
  })
  // EndDateArea is the area that belongs to the dates after the scheduling end date.
  const EndDateAreaStart = derived([EndDate, CurrentDisplayedEndDate, CurrentDisplayedStartDate, GetAdjustedDateInclDaysOff], ([EndDate, CurrentDisplayedEndDate, CurrentDisplayedStartDate, GetAdjustedDateInclDaysOff]) => {
    return Math.max(0, 100 * GanttHelper._gantHelper_scale(GetAdjustedDateInclDaysOff(EndDate).getTime(), CurrentDisplayedEndDate.getTime(), CurrentDisplayedStartDate.getTime()))
  })
  // FreePlanStartDateAreaEnd is the area that belongs to the dates before the start date of the uploaded period.
  const FreePlanStartDateAreaEnd = derived([ValidStartDate, CurrentDisplayedEndDate, CurrentDisplayedStartDate, GetAdjustedDateInclDaysOff], ([ValidStartDate, CurrentDisplayedEndDate, CurrentDisplayedStartDate, GetAdjustedDateInclDaysOff]) => {
    return Math.max(0, 100 * GanttHelper._gantHelper_scale(GetAdjustedDateInclDaysOff(ValidStartDate).getTime(), CurrentDisplayedEndDate.getTime(), CurrentDisplayedStartDate.getTime()));
  })
  // FreePlanEndDateAreaStart is the area that belongs to the dates after the end date of the uploaded period.
  const FreePlanEndDateAreaStart = derived([ValidEndDate, CurrentDisplayedEndDate, CurrentDisplayedStartDate, GetAdjustedDateInclDaysOff], ([ValidEndDate, CurrentDisplayedEndDate, CurrentDisplayedStartDate, GetAdjustedDateInclDaysOff]) => {
    return Math.min(100, 100 * GanttHelper._gantHelper_scale(GetAdjustedDateInclDaysOff(ValidEndDate).getTime(), CurrentDisplayedEndDate.getTime(), CurrentDisplayedStartDate.getTime()))
  })
  // Display zones
  $: AdjustStamp = (stamp) => GanttHelper._gantHelper_adjustDateWithinADay(stamp, $StartDayDisplayTime, $EndDayDisplayTime, $DayStartTime);
  $: CalculateStartX = (itemStart) => Math.min(100, 100 * GanttHelper._gantHelper_scale(AdjustStamp(itemStart).getTime(), $CurrentDisplayedEndDate.getTime(), $CurrentDisplayedStartDate.getTime()));


  let rowLabels = [];
  let currentDate = new Date();

  const GeneralZoom = (e) => {
    e.preventDefault();

    if (e.ctrlKey) {
      // Row nb scroll
      if (e.deltaY > 0)
        GanttStore.DisplayedRowCount.update((n) => (n + 1 >= 100) ? n : n + 1);
      else if (e.deltaY < 0)
        GanttStore.DisplayedRowCount.update((n) => (n > 1) ? n - 1 : n);
      // Zoom
      if (e.deltaY > 0)
        GanttStore.DisplayedSpanInSeconds.update((d) => Math.min(d * 1.05, GanttHelper.GHYEAR));
      else if (e.deltaY < 0)
        GanttStore.DisplayedSpanInSeconds.update((d) => Math.max(d / 1.05, GanttHelper.GHSECOND));
    }
  }

  const ShowLabel = derived(GanttStore.CurrentStyleInstance, ($CurrentStyleInstance) => {
    return $CurrentStyleInstance?.ShowLabel;
  });

  onMount(() => {
    currentDate = new Date();
		const nowInterval = setInterval(() => {
			currentDate = new Date();
		}, 10000);

		return () => clearInterval(nowInterval);
	});

</script>

<svg id="gantt_chartarea_chartcontent" class="pullable" on:wheel={GeneralZoom} xmlns="http://www.w3.org/2000/svg">
  <pattern
    id="stripe"
    patternUnits="userSpaceOnUse"
    width="6.5"
    height="6.5"
    patternTransform="rotate(45)"
  >
    <line
      x1="0"
      y="0"
      x2="0"
      y2="6.5"
      stroke="rgba(0, 0, 0, 0.45)"
      stroke-width="1"
    />
  </pattern>

  <pattern id="gridPattern" width="10" height="10" patternUnits="userSpaceOnUse">
      <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" stroke-width="0.5" />
  </pattern>

  <pattern id="checked" width="3" height="3" stroke="white" stroke-linecap="square" stroke-width="2" patternTransform="rotate(45) scale(2)"
      patternUnits="userSpaceOnUse">
    <line x1="0" y1="0" x2="0" y2="3"></line>
    <line x1="0" y1="0" x2="3" y2="0"></line>
  </pattern>

  <!-- Now date orange marker -->
  <marker
    id="nowMarker"
    viewBox="0 0 12 12"
    refX="6"
    refY="6"
    markerWidth="12"
    markerHeight="12"
    markerUnits="userSpaceOnUse"
    orient="auto">
    <polygon points="0 6,6 12,12 6,6 0" style=" fill: #FF9F00"/>
  </marker>

  <!-- LET marker of red down-pointing triangle for OrderGantt charts -->
  <symbol id="marker_let" width="22" height="22" viewBox="0 0 22 22" fill="none" x="-11">
    <path d="M21.1328 3.5L11 21.0021L0.867223 3.5L21.1328 3.5Z" fill="#FF0000" stroke="white"/>
  </symbol>

  <!-- EST marker of blue down-pointing triangle for OrderGantt charts -->
  <symbol id="marker_let" width="22" height="22" viewBox="0 0 22 22" fill="none" x="-11">
    <path d="M21.1328 3.5L11 21.0021L0.867223 3.5L21.1328 3.5Z" fill="#0057FF" stroke="white"/>
  </symbol>


  <mask id="checked-mask" x="0" y="0" width="1" height="1" >
    <rect x="0" y="0" width="100%" height="100%" fill="url(#checked)" />
  </mask>

  {#if $StartDateAreaEnd > 0}
    <rect
      x="0"
      y="0"
      height="100%"
      width="{$StartDateAreaEnd}%"
      fill="#ffddcb"
      pointer-events="none"
    />
  {/if}
  {#if $EndDateAreaStart < 100}
    <rect
      x="{$EndDateAreaStart}%"
      y="0"
      height="100%"
      width={(100 - $EndDateAreaStart) + "%"}
      fill="#cbddff"
      pointer-events="none"
    />
  {/if}
  {#each $InternalLowerSubSplits as tp}
    {#if !$InternalLowerSplits.some((item) => item.EndX === CalculateStartX(tp))}
    <line
      x1="{CalculateStartX(tp)}%"
      y1="0%"
      x2="{CalculateStartX(tp)}%"
      y2="100%"
      stroke="#cccccc"
      stroke-width="1"
      stroke-dasharray="4"
    />
    {/if}
  {/each}
  {#each $InternalLowerSplits as vsplit}
    <line
      x1="{vsplit.EndX}%"
      x2="{vsplit.EndX}%"
      y1="0"
      y2="100%"
      stroke="rgba(0,0,0,0.2)"
      stroke-width="1"
    />
  {/each}
  {#if $FreePlanStartDateAreaEnd > 0}
    <rect
      x="0"
      y="0"
      height="100%"
      width="{$FreePlanStartDateAreaEnd}%"
      fill="rgba(128, 128, 128, 0.5)"
      pointer-events="none"
    />
  {/if}
  {#if $FreePlanEndDateAreaStart < 100}
    <rect
      x="{$FreePlanEndDateAreaStart}%"
      y="0"
      height="100%"
      width={(100 - $FreePlanEndDateAreaStart) + "%"}
      fill="rgba(128, 128, 128, 0.5)"
      pointer-events="none"
    />
  {/if}

  {#if $GanttChartType !== GanttStore.GanttChartTypes.SimpleResGantt
  && $GanttChartType !== GanttStore.GanttChartTypes.SimpleOrderGantt}
    <line
      x1="{CalculateStartX($GetAdjustedDateInclDaysOff(currentDate))}%"
      y1="0%"
      x2="{CalculateStartX($GetAdjustedDateInclDaysOff(currentDate))}%"
      y2="100%"
      style="stroke: #FF9F00; stroke-width: 2px"
      marker-end="url(#nowMarker)"
    />
  {/if}

  {#if $GanttChartType === GanttStore.GanttChartTypes.SimpleOrderGantt}
      <line
        x1="{CalculateStartX($GetAdjustedDateInclDaysOff(roundToNearestTime(currentDate, 1, false)))}%"
        y1="0%"
        x2="{CalculateStartX($GetAdjustedDateInclDaysOff(roundToNearestTime(currentDate, 1, false)))}%"
        y2="100%"
        style="stroke: #FF9F00; stroke-width: 2px"
        marker-end="url(#nowMarker)"
      />
  {/if}

  {#if $GanttChartType === GanttStore.GanttChartTypes.SimpleResGantt}
      <line
        x1="{CalculateStartX($GetAdjustedDateInclDaysOff(roundToNearestTime(currentDate, 4, false)))}%"
        y1="0%"
        x2="{CalculateStartX($GetAdjustedDateInclDaysOff(roundToNearestTime(currentDate, 4, false)))}%"
        y2="100%"
        style="stroke: #FF9F00; stroke-width: 2px"
        marker-end="url(#nowMarker)"
      />
  {/if}


    {#each $RowsCurrentlyDisplayed as row, index}
      {#if row !== null && $RowDownloadUrl !== ""}
          <ChartContentRow
            GanttStore={GanttStore}
            Row={row.Row}
            RowType={row.RowType}
            Alias={row.Alias}
            IsNull={row === null}
            Height={($DisplayedRowCount > 0) ? 100 / $DisplayedRowCount : 100}
            OriginY={(($DisplayedRowCount > 0) ? 100 / $DisplayedRowCount : 100) * index}
            DownloadUrl={$RowDownloadUrl + row.Row + ".json"}
            StartDate={$CurrentDisplayedStartDate}
            EndDate={$CurrentDisplayedEndDate}
            AdjustStamp={AdjustStamp}
            IsOperation={row.IsOperation}
            Index={index}
            bind:rowLabels
          />
      {/if}
    {/each}


    {#if $GanttChartType === GanttStore.GanttChartTypes.SimpleResGantt
      || $GanttChartType === GanttStore.GanttChartTypes.ResGantt
      || $GanttChartType === GanttStore.GanttChartTypes.CustomizedResGantt
    }
      <ResourceConnectionLines
        GanttStore={GanttStore}
        AdjustStamp={AdjustStamp}
        RowDownloadUrl={$RowDownloadUrl}
      />
    {/if}

    {#if $GanttChartType === GanttStore.GanttChartTypes.OrderGantt || $GanttChartType === GanttStore.GanttChartTypes.PeggedOrderGantt}
      <OrderConnectionLines
        {GanttStore}
        {AdjustStamp}
        RowDownloadUrl={$RowDownloadUrl}
      />
    {/if}

    {#if $ShowLabel && $GanttChartType !== GanttStore.GanttChartTypes.SimpleResGantt}
      {#each rowLabels as row, index}
        {#if row != undefined && $RowsCurrentlyDisplayed[index] != null}
          {#each row as label}
            <ChartLabel
              GanttStore={GanttStore}
              Label={label}
              StartDate={$CurrentDisplayedStartDate}
              EndDate={$CurrentDisplayedEndDate}
              Height={($DisplayedRowCount > 0) ? 100 / $DisplayedRowCount : 100}
              OriginY={(($DisplayedRowCount > 0) ? 100 / $DisplayedRowCount : 100) * index}
              AdjustStamp={AdjustStamp}
              CurrentStyle={$CurrentStyle.StyleName}
            />
          {/each}
        {/if}
      {/each}
    {/if}

</svg>

<style>
  svg#gantt_chartarea_chartcontent {
    width: 100%;
    flex: 1;
  }
</style>
